import { useState, useEffect } from 'react'
import { 
  Ship, 
  Globe, 
  FlaskConical, 
  Users,
  TrendingUp,
  AlertTriangle,
  Star,
  Coins,
  Zap,
  Gem,
  Crown,
  Activity,
  Target,
  Rocket,
  Shield
} from 'lucide-react'
import { Button } from '../components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { Badge } from '../components/ui/badge'
import { Link } from 'react-router-dom'
import { db } from '../services/supabase'
import gameAPI from '../services/GameAPI'
import WorkingAPI from '../services/WorkingAPI'
import GlassCard from '../components/GlassCard'
import StatCard from '../components/StatCard'
import LoadingSpinner from '../components/LoadingSpinner'
import Starfield from '../components/Starfield'
import ResourceDisplay from '../components/ResourceDisplay'
import { useGameState } from '../contexts/GameStateContext'

interface User {
  id: string
  name: string
  email: string
  level: number
  faction?: string
}

interface DashboardProps {
  user: User
}

const Dashboard = ({ user }: DashboardProps) => {
  const [notifications, setNotifications] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showAllNotifications, setShowAllNotifications] = useState(false)
  const [dashboardData, setDashboardData] = useState<any>(null)
  const { stats, fleets, isOnline, lastUpdate } = useGameState()
  const workingAPI = WorkingAPI.getInstance()

  // Handle viewing all notifications
  const handleViewAllNotifications = async () => {
    try {
      const result = await gameAPI.getNotifications(user.id)
      if (result.status === 200 && result.data) {
        setNotifications(result.data)
        setShowAllNotifications(true)
      }
    } catch (error) {
      console.error('Failed to load all notifications:', error)
    }
  }

  // Handle marking notification as read
  const handleMarkAsRead = async (notificationId: string) => {
    try {
      await gameAPI.markNotificationAsRead(notificationId)
      setNotifications(prev => 
        prev.map(notif => 
          notif.id === notificationId ? { ...notif, read: true } : notif
        )
      )
    } catch (error) {
      console.error('Failed to mark notification as read:', error)
    }
  }

  // Load dashboard data
  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        setIsLoading(true)

        // Try to get dashboard data from working API
        const dashboardResult = await workingAPI.getDashboardData()
        
        if (dashboardResult.status === 200 && dashboardResult.data) {
          // Use real dashboard data from API
          setDashboardData(dashboardResult.data)
          if (dashboardResult.data.notifications) {
            setNotifications(dashboardResult.data.notifications.slice(0, 5))
          }
        } else {
          console.log('Dashboard API response:', dashboardResult)
          
          // Use fallback data with API status information
          setNotifications([
            { 
              id: 1, 
              type: 'info', 
              title: 'API Status', 
              message: `Dashboard API: ${dashboardResult.error || 'Connection successful but needs implementation'}`, 
              created_at: new Date().toISOString() 
            },
            { id: 2, type: 'welcome', title: 'Welcome Commander!', message: 'Your galactic empire awaits. Backend APIs are connecting...', created_at: new Date().toISOString() },
            { id: 3, type: 'fleet', title: 'Fleet Status', message: `${fleets.length} fleets ready for deployment`, created_at: new Date().toISOString() },
            { id: 4, type: 'info', title: 'Real-time Updates', message: `Resources updating locally • Last update: ${lastUpdate.toLocaleTimeString()}`, created_at: new Date().toISOString() }
          ])
        }

        // Track dashboard visit for analytics (fallback to original API)
        await gameAPI.trackEvent('dashboard_visit', { 
          timestamp: new Date().toISOString(),
          fleet_count: fleets.length,
          credits: stats.credits 
        }).catch(err => console.warn('Analytics tracking failed:', err))

      } catch (error) {
        console.error('Failed to load dashboard data:', error)
        // Keep default values on error
        setNotifications([
          { id: 1, type: 'error', title: 'Connection Error', message: 'Unable to connect to backend services', created_at: new Date().toISOString() },
          { id: 2, type: 'info', title: 'Local Mode', message: 'Running in local mode with cached data', created_at: new Date().toISOString() }
        ])
      } finally {
        setIsLoading(false)
      }
    }

    loadDashboardData()
  }, [user.id, fleets.length, lastUpdate, stats.credits])



  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'fleet': return Ship
      case 'research': return FlaskConical
      case 'alert': return AlertTriangle
      case 'diplomacy': return Users
      default: return Star
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0a0a0a] via-[#1a1a2e] to-[#16213e] relative overflow-hidden">
        <Starfield density={200} speed={0.5} />
        <div className="relative z-10 pt-16">
          <div className="container mx-auto px-4 py-8 flex items-center justify-center min-h-[50vh]">
            <LoadingSpinner 
              size="xl" 
              variant="quantum" 
              color="purple" 
              message="Initializing your galactic empire..."
            />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a0a0a] via-[#1a1a2e] to-[#16213e] relative overflow-hidden">
      <Starfield density={300} speed={0.8} />
      <div className="relative z-10 pt-16">
        <div className="container mx-auto px-4 py-8">
          {/* Header */}
          <GlassCard variant="premium" className="mb-8">
            <div className="text-center">
              <h1 className="text-4xl font-bold text-white mb-2 flex items-center justify-center gap-3">
                <Crown className="w-8 h-8 text-yellow-400" />
                Welcome back, {user.name}
              </h1>
              <p className="text-purple-300 text-lg">
                {user.faction} • Level {user.level} • Galactic Command Center
              </p>
            </div>
          </GlassCard>

          {/* Enhanced Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 mb-8">
            <StatCard
              title="Fleet Power"
              value={stats.ships}
              icon={<Ship className="w-6 h-6" />}
              variant="premium"
              trend="up"
              change={{ value: 12, percentage: false, period: "this week" }}
              description="Active starships under your command"
            />
            
            <StatCard
              title="Territories"
              value={stats.planets}
              icon={<Globe className="w-6 h-6" />}
              variant="epic"
              trend="neutral"
              description="Colonized worlds in your empire"
            />
            
            <StatCard
              title="Research"
              value={`${stats.research}%`}
              icon={<FlaskConical className="w-6 h-6" />}
              variant="premium"
              trend="up"
              change={{ value: 8, percentage: true, period: "this month" }}
              description="Scientific advancement progress"
            />
            
            <StatCard
              title="Credits"
              value={stats.credits}
              icon={<Coins className="w-6 h-6" />}
              variant="legendary"
              trend="up"
              change={{ value: 2500, percentage: false, period: "today" }}
              description="Galactic currency reserves"
            />
            
            <StatCard
              title="Energy"
              value={stats.energy}
              icon={<Zap className="w-6 h-6" />}
              variant="premium"
              trend="down"
              change={{ value: -50, percentage: false, period: "per hour" }}
              description="Power grid output levels"
            />
            
            <StatCard
              title="Minerals"
              value={stats.minerals}
              icon={<Gem className="w-6 h-6" />}
              variant="epic"
              trend="up"
              change={{ value: 15, percentage: true, period: "this cycle" }}
              description="Raw material stockpiles"
            />
          </div>

          {/* Enhanced Activity Dashboard */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            <GlassCard variant="premium" className="col-span-1">
              <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-green-400" />
                Economic Performance
              </h2>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-300">Credits per Hour</span>
                  <span className="text-yellow-400 font-bold">+2,500</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-300">Resource Efficiency</span>
                  <span className="text-green-400 font-bold">94%</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-300">Trade Volume</span>
                  <span className="text-blue-400 font-bold">158K</span>
                </div>
              </div>
            </GlassCard>

            <GlassCard variant="epic">
              <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-orange-400" />
                Priority Alerts
              </h2>
              <div className="space-y-3">
                {notifications.length > 0 ? (
                  notifications.slice(0, 4).map((notification, index) => (
                    <div key={index} className="flex items-start gap-3 p-3 bg-white/5 rounded-lg border border-white/10">
                      <div className="w-2 h-2 bg-orange-400 rounded-full mt-2 flex-shrink-0"></div>
                      <div>
                        <div className="text-white font-medium">{notification.title}</div>
                        <div className="text-gray-400 text-sm">{notification.message}</div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-4">
                    <div className="text-gray-400">All systems operational</div>
                    <div className="text-green-400 text-sm">No alerts at this time</div>
                  </div>
                )}
              </div>
            </GlassCard>
          </div>

          {/* Quick Actions */}
          <GlassCard variant="default" className="mb-8">
            <h2 className="text-xl font-bold text-white mb-6 text-center">Command Center</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Link to="/fleet">
                <Button variant="outline" className="w-full h-20 bg-blue-500/10 border-blue-500/30 hover:bg-blue-500/20 text-blue-400 hover:text-blue-300">
                  <div className="text-center">
                    <Ship className="w-6 h-6 mx-auto mb-1" />
                    <div className="text-sm font-medium">Fleet</div>
                  </div>
                </Button>
              </Link>
              
              <Link to="/research">
                <Button variant="outline" className="w-full h-20 bg-purple-500/10 border-purple-500/30 hover:bg-purple-500/20 text-purple-400 hover:text-purple-300">
                  <div className="text-center">
                    <FlaskConical className="w-6 h-6 mx-auto mb-1" />
                    <div className="text-sm font-medium">Research</div>
                  </div>
                </Button>
              </Link>
              
              <Link to="/galaxy">
                <Button variant="outline" className="w-full h-20 bg-green-500/10 border-green-500/30 hover:bg-green-500/20 text-green-400 hover:text-green-300">
                  <div className="text-center">
                    <Globe className="w-6 h-6 mx-auto mb-1" />
                    <div className="text-sm font-medium">Galaxy</div>
                  </div>
                </Button>
              </Link>
              
              <Link to="/alliances">
                <Button variant="outline" className="w-full h-20 bg-yellow-500/10 border-yellow-500/30 hover:bg-yellow-500/20 text-yellow-400 hover:text-yellow-300">
                  <div className="text-center">
                    <Users className="w-6 h-6 mx-auto mb-1" />
                    <div className="text-sm font-medium">Alliances</div>
                  </div>
                </Button>
              </Link>
            </div>
          </GlassCard>

          {/* Activity and Notifications Section */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              {/* Recent Activity */}
              <GlassCard variant="default" className="mb-6">
                <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <Activity className="w-5 h-5 text-blue-400" />
                  Recent Activity
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
                    <Ship className="w-5 h-5 text-blue-400" />
                    <div>
                      <div className="text-white font-medium">Fleet deployed to System X-7</div>
                      <div className="text-gray-400 text-sm">2 hours ago</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
                    <FlaskConical className="w-5 h-5 text-purple-400" />
                    <div>
                      <div className="text-white font-medium">Research completed: Advanced Shields</div>
                      <div className="text-gray-400 text-sm">4 hours ago</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
                    <Globe className="w-5 h-5 text-green-400" />
                    <div>
                      <div className="text-white font-medium">New planet colonized: Kepler-442b</div>
                      <div className="text-gray-400 text-sm">6 hours ago</div>
                    </div>
                  </div>
                </div>
              </GlassCard>
            </div>

            {/* Notifications Sidebar */}
            <div>
              <GlassCard variant="default">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-white">Notifications</h3>
                  <AlertTriangle className="w-5 h-5 text-orange-400" />
                </div>
                <div className="space-y-3">
                  {notifications.length > 0 ? (
                    notifications.slice(0, 3).map((notification, index) => {
                      const Icon = getNotificationIcon(notification.type)
                      const timeAgo = notification.created_at 
                        ? new Date(notification.created_at).toLocaleString()
                        : notification.time || 'Just now'
                        
                      return (
                        <div key={notification.id || index} className={`flex items-start space-x-3 p-3 rounded-lg ${notification.urgent ? 'bg-red-500/10 border border-red-500/30' : 'bg-white/5'}`}>
                          <Icon className={`w-4 h-4 mt-0.5 ${notification.urgent ? 'text-red-400' : 'text-gray-400'}`} />
                          <div className="flex-1">
                            <p className="text-sm text-white">{notification.message}</p>
                            <p className="text-xs text-gray-400 mt-1">{timeAgo}</p>
                          </div>
                        </div>
                      )
                    })
                  ) : (
                    <div className="text-center py-4">
                      <div className="text-gray-400">All systems operational</div>
                      <div className="text-green-400 text-sm">No alerts at this time</div>
                    </div>
                  )}
                </div>
                <div className="mt-4">
                  <Button 
                    variant="ghost" 
                    className="w-full text-sm"
                    onClick={handleViewAllNotifications}
                  >
                    {showAllNotifications ? 'Showing All' : 'View All Notifications'}
                  </Button>
                </div>
              </GlassCard>

              {/* Today's Progress */}
              <GlassCard variant="premium" className="mt-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-white">Today's Progress</h3>
                  <TrendingUp className="w-5 h-5 text-green-400" />
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-300">Ships Built</span>
                    <span className="text-green-400 font-semibold">+3</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-300">Systems Explored</span>
                    <span className="text-blue-400 font-semibold">+2</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-300">Research Points</span>
                    <span className="text-purple-400 font-semibold">+240</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-300">Credits Earned</span>
                    <span className="text-yellow-400 font-semibold">+15,000</span>
                  </div>
                </div>
              </GlassCard>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Dashboard